import { Box, Button, CircularProgress, Typography } from "@material-ui/core";
import { useState } from "react";
import {
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  rem,
} from "../../_lib/epocus-palette";
import { Card, PageRoot } from "../../_lib/stat-primitives";
import {
  selectActiveProgram,
  useMyPrograms,
} from "../../_lib/use-active-program";
import { useProgramDirectory } from "../../_lib/use-program-directory";
import { displayName, formatMinutes, formatPct } from "../_lib/admin-metrics";
import {
  CardHeading,
  CardNote,
  EmptyRow,
  KpiCard,
  KpiGrid,
  PageTitle,
  ScrollTable,
  ShareBar,
  StatusPill,
  TABLE_CELL_STYLE,
} from "../_lib/admin-primitives";
import { usePendingEncounters } from "../_lib/use-encounter-assignment";
import { useEpocusProgramQaWorkloadQuery } from "./_lib/__generated__/qa-workload-operations.gql";
import { ReassignSheet, type ReassignTarget } from "./_lib/reassign-sheet";

export const EpocusQaWorkloadPage: FC = () => {
  const { programs, loading: programsLoading } = useMyPrograms();
  const program = selectActiveProgram(programs);
  const programId = program?.id ?? null;

  const { data, loading, error } = useEpocusProgramQaWorkloadQuery({
    variables: { programId: programId ?? "" },
    skip: !programId,
    fetchPolicy: "cache-and-network",
  });

  const { reviewers } = useProgramDirectory(programId);
  const { assignedTo, pool } = usePendingEncounters();
  const [target, setTarget] = useState<ReassignTarget | null>(null);

  const workload = data?.epocusProgramQaWorkload;

  return (
    <PageRoot>
      <PageTitle
        title="QA workload"
        subtitle="Pending review across the whole program"
      />

      {!workload && (programsLoading || loading) ? (
        <Box style={{ display: "flex", justifyContent: "center", padding: 32 }}>
          <CircularProgress aria-label="Loading the QA workload" />
        </Box>
      ) : !programId ? (
        <Card>
          <CardNote>Couldn't determine your program.</CardNote>
        </Card>
      ) : !workload && error ? (
        <Card>
          <CardNote>Couldn't load the QA workload.</CardNote>
        </Card>
      ) : workload ? (
        <>
          <KpiGrid>
            <KpiCard
              label="Pending across program"
              value={String(workload.pending)}
              caption="all reviewers, all sources"
            />
            <KpiCard
              label="Overdue"
              value={String(workload.overdue)}
              caption={
                workload.turnaroundTargetMinutes === null
                  ? "no turnaround target set"
                  : "past target"
              }
            />
            <KpiCard
              label="Active reviewers"
              value={String(workload.activeReviewers)}
              caption={`open work, or signed in ${workload.activeReviewerWindowDays} days`}
            />
            <KpiCard
              label="Unassigned in pool"
              value={String(workload.unassignedInPool)}
              caption="awaiting pickup"
            />
          </KpiGrid>

          <Card>
            <CardHeading
              title="Workload by reviewer"
              hint={`turnaround over ${workload.turnaroundWindowDays} days`}
            />
            <ScrollTable
              columns={[
                "Reviewer",
                "Open",
                "Status",
                "Median TT",
                "Within target",
                "",
              ]}
            >
              {workload.byReviewer.length === 0 ? (
                <EmptyRow colSpan={6}>
                  No reviewer holds open work in this program.
                </EmptyRow>
              ) : (
                workload.byReviewer.map((reviewer) => {
                  const name = displayName(reviewer.name, "reviewer");
                  return (
                    <tr key={reviewer.reviewerId}>
                      <td style={{ ...TABLE_CELL_STYLE, fontWeight: 600 }}>
                        {name}
                      </td>
                      <td style={TABLE_CELL_STYLE}>{reviewer.open}</td>
                      <td style={TABLE_CELL_STYLE}>
                        {reviewer.overdue > 0 ? (
                          <StatusPill
                            tone="risk"
                            label={`${reviewer.overdue} overdue`}
                          />
                        ) : (
                          <StatusPill tone="ok" label="none overdue" />
                        )}
                      </td>
                      <td style={TABLE_CELL_STYLE}>
                        {formatMinutes(reviewer.medianTurnaroundMinutes)}
                      </td>
                      <td style={TABLE_CELL_STYLE}>
                        <ShareBar
                          value={reviewer.withinTargetPct ?? null}
                          label={formatPct(reviewer.withinTargetPct)}
                        />
                      </td>
                      <td style={{ ...TABLE_CELL_STYLE, textAlign: "right" }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() =>
                            setTarget({
                              reviewerId: reviewer.reviewerId,
                              reviewerName: name,
                              encounters: assignedTo(reviewer.reviewerId),
                            })
                          }
                        >
                          Reassign
                        </Button>
                      </td>
                    </tr>
                  );
                })
              )}
            </ScrollTable>
          </Card>

          <Card>
            <Box
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                marginBottom: 12,
              }}
            >
              <Box>
                <Typography
                  style={{
                    fontSize: rem(15),
                    fontWeight: 700,
                    color: EPOCUS_TEXT_PRIMARY,
                  }}
                >
                  Pool
                </Typography>
                <Typography
                  style={{ fontSize: rem(12), color: EPOCUS_TEXT_SECONDARY }}
                >
                  Pending studies nobody is assigned to
                </Typography>
              </Box>
              <Button
                size="small"
                variant="contained"
                color="primary"
                disabled={pool.length === 0}
                onClick={() =>
                  setTarget({
                    reviewerId: null,
                    reviewerName: "the pool",
                    encounters: pool,
                  })
                }
              >
                Assign
              </Button>
            </Box>
            {pool.length === 0 ? (
              <CardNote>Nothing waiting in the pool.</CardNote>
            ) : (
              <CardNote>
                {pool.length} {pool.length === 1 ? "study" : "studies"} waiting
                for a reviewer to pick up.
              </CardNote>
            )}
          </Card>
        </>
      ) : null}

      <ReassignSheet
        target={target}
        reviewers={reviewers}
        onClose={() => setTarget(null)}
      />
    </PageRoot>
  );
};
